/**
 * WhatsApp CSAT prompts view — Phase 2.3.j.3.
 *
 * Owns the inline "Customer satisfaction prompts" section that renders
 * inside the WhatsApp channel-config card. Manages a list of templated
 * CSAT message bodies (one per code), with add/remove/reset-to-defaults
 * controls and a save action that posts the full list to the server.
 *
 * The section is rendered dynamically into the `<details>` element of
 * the WhatsApp config card by `renderWhatsAppCsatPrompts(details)` —
 * channel-settings call this directly when the WhatsApp card is built.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `showFlash` (default already alias).
 *
 * The cluster shares `channelSettingsState` with templates + AI prompts;
 * the module reads/writes `state.whatsappCsatPrompts`,
 * `state.whatsappCsatDefaults`, `state.loadingWhatsAppCsat`,
 * `state.whatsappCsatFetched`, `state.whatsappCsatLoadFailed` via an
 * injected getter (same pattern as 2.3.j.2).
 *
 * Phase 3.2.c: dropped the empty `configure()` no-op and removed the
 * configureChannelSettingsState injection — channel-settings is the
 * canonical state owner; this module reads through it lazily.
 */

import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // Lazy state reach-through. The channel-settings view owns
  // `channelSettingsState`; this module reads/writes the WhatsApp-CSAT
  // slice directly through that view's `getState()` surface.
  const getChannelSettingsState = () => {
    const cs = window.adminViews && window.adminViews.channelSettings;
    return cs && typeof cs.getState === 'function' ? cs.getState() : {
      whatsappCsatPrompts: [],
      whatsappCsatDefaults: [],
      loadingWhatsAppCsat: false,
      whatsappCsatFetched: false,
      whatsappCsatLoadFailed: false,
    };
  };
  const renderChannelConfigs = () => {
    const cs = window.adminViews && window.adminViews.channelSettings;
    if (cs && cs.renderChannelConfigs) cs.renderChannelConfigs();
  };

  // Compat alias — lifted bodies reference `channelSettingsState` directly.
  const channelSettingsState = new Proxy({}, {
    get(_, prop) { return getChannelSettingsState()[prop]; },
    set(_, prop, value) { getChannelSettingsState()[prop] = value; return true; },
  });

    function cloneWhatsAppPrompt(item) {
      return {
        code: (item && item.code) ? String(item.code) : '',
        body_template: (item && item.body_template) ? String(item.body_template) : '',
        locale: (item && item.locale) ? String(item.locale) : 'en',
        enabled: !(item && item.enabled === false),
      };
    }

    function getWhatsAppCsatPrompts() {
      return Array.isArray(channelSettingsState.whatsappCsatPrompts)
        ? channelSettingsState.whatsappCsatPrompts
        : [];
    }

    function renderWhatsAppCsatPrompts(details) {
      if (!details) return;
      const existing = details.querySelector('.whatsapp-csat-section');
      if (existing) existing.remove();

      const section = document.createElement('div');
      section.className = 'whatsapp-csat-section';
      section.style.marginTop = '16px';
      section.style.paddingTop = '12px';
      section.style.borderTop = '1px solid rgba(148, 163, 184, 0.3)';

      const heading = document.createElement('h4');
      heading.textContent = 'Customer satisfaction prompts';
      heading.style.margin = '0 0 4px';
      section.appendChild(heading);

      const description = document.createElement('p');
      description.textContent = 'These templates are sent on WhatsApp after resolutions or technician visits.';
      description.style.margin = '0 0 12px';
      description.style.fontSize = '12px';
      description.style.color = 'var(--sub)';
      section.appendChild(description);

      if (channelSettingsState.loadingWhatsAppCsat) {
        const loading = document.createElement('div');
        loading.textContent = 'Loading CSAT prompts…';
        loading.style.fontSize = '12px';
        loading.style.color = 'var(--sub)';
        section.appendChild(loading);
        details.appendChild(section);
        return;
      }

      if (channelSettingsState.whatsappCsatLoadFailed) {
        const error = document.createElement('div');
        error.textContent = 'Failed to load prompts. Check your connection or retry.';
        error.style.fontSize = '12px';
        error.style.color = 'var(--danger, #ef4444)';
        error.style.marginBottom = '8px';
        section.appendChild(error);

        const retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.className = 'btn';
        retryBtn.textContent = 'Retry load';
        retryBtn.addEventListener('click', reloadWhatsAppCsatPrompts);
        section.appendChild(retryBtn);

        details.appendChild(section);
        return;
      }

      const prompts = getWhatsAppCsatPrompts();
      if (!prompts.length) {
        const empty = document.createElement('div');
        empty.textContent = 'No prompts configured.';
        empty.style.fontSize = '12px';
        empty.style.color = 'var(--sub)';
        empty.style.marginBottom = '12px';
        section.appendChild(empty);
      }

      prompts.forEach((prompt, index) => {
        const card = document.createElement('div');
        card.className = 'csat-prompt-card';
        card.style.border = '1px solid rgba(148, 163, 184, 0.35)';
        card.style.borderRadius = '8px';
        card.style.padding = '12px';
        card.style.marginBottom = '12px';
        card.style.background = 'rgba(15, 23, 42, 0.3)';

        const header = document.createElement('div');
        header.style.display = 'flex';
        header.style.gap = '12px';
        header.style.alignItems = 'center';
        header.style.marginBottom = '8px';

        const codeField = document.createElement('div');
        codeField.className = 'field';
        const codeLabel = document.createElement('span');
        codeLabel.textContent = 'Code';
        codeField.appendChild(codeLabel);
        const codeInput = document.createElement('input');
        codeInput.type = 'text';
        codeInput.value = prompt.code || '';
        codeInput.placeholder = 'post_resolution_default';
        codeInput.addEventListener('input', (evt) => {
          const value = (evt.target.value || '').trim();
          const list = getWhatsAppCsatPrompts();
          if (list[index]) list[index].code = value;
        });
        codeField.appendChild(codeInput);
        header.appendChild(codeField);

        const localeField = document.createElement('div');
        localeField.className = 'field';
        localeField.style.maxWidth = '120px';
        const localeLabel = document.createElement('span');
        localeLabel.textContent = 'Locale';
        localeField.appendChild(localeLabel);
        const localeInput = document.createElement('input');
        localeInput.type = 'text';
        localeInput.value = prompt.locale || 'en';
        localeInput.addEventListener('input', (evt) => {
          const value = (evt.target.value || '').trim() || 'en';
          const list = getWhatsAppCsatPrompts();
          if (list[index]) list[index].locale = value;
        });
        localeField.appendChild(localeInput);
        header.appendChild(localeField);

        const enabledField = document.createElement('label');
        enabledField.className = 'switch';
        enabledField.style.marginTop = '20px';
        const enabledInput = document.createElement('input');
        enabledInput.type = 'checkbox';
        enabledInput.checked = prompt.enabled !== false;
        enabledInput.addEventListener('change', (evt) => {
          const list = getWhatsAppCsatPrompts();
          if (list[index]) list[index].enabled = evt.target.checked;
        });
        const slider = document.createElement('span');
        slider.className = 'slider';
        enabledField.appendChild(enabledInput);
        enabledField.appendChild(slider);
        const enabledCaption = document.createElement('span');
        enabledCaption.textContent = 'Enabled';
        enabledCaption.style.marginLeft = '8px';
        enabledCaption.style.fontSize = '12px';
        const enabledWrap = document.createElement('div');
        enabledWrap.style.display = 'flex';
        enabledWrap.style.alignItems = 'center';
        enabledWrap.style.gap = '8px';
        enabledWrap.appendChild(enabledField);
        enabledWrap.appendChild(enabledCaption);
        header.appendChild(enabledWrap);

        if (prompts.length > 1) {
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'btn ghost';
          removeBtn.textContent = 'Remove';
          removeBtn.style.marginLeft = 'auto';
          removeBtn.addEventListener('click', () => removeWhatsAppCsatPrompt(index));
          header.appendChild(removeBtn);
        }

        card.appendChild(header);

        const bodyField = document.createElement('label');
        bodyField.className = 'field';
        bodyField.innerHTML = '<span>Body template</span>';
        const bodyTextarea = document.createElement('textarea');
        bodyTextarea.value = prompt.body_template || '';
        bodyTextarea.rows = 3;
        bodyTextarea.addEventListener('input', (evt) => {
          const list = getWhatsAppCsatPrompts();
          if (list[index]) list[index].body_template = evt.target.value;
        });
        bodyField.appendChild(bodyTextarea);
        card.appendChild(bodyField);

        section.appendChild(card);
      });

      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.gap = '8px';
      actions.style.flexWrap = 'wrap';

      const saveBtn = document.createElement('button');
      saveBtn.type = 'button';
      saveBtn.className = 'btn primary';
      saveBtn.textContent = 'Save CSAT prompts';
      saveBtn.addEventListener('click', () => saveWhatsAppCsatPrompts(saveBtn));
      actions.appendChild(saveBtn);

      const resetBtn = document.createElement('button');
      resetBtn.type = 'button';
      resetBtn.className = 'btn ghost';
      resetBtn.textContent = 'Reset to defaults';
      resetBtn.addEventListener('click', resetWhatsAppCsatPromptsToDefaults);
      actions.appendChild(resetBtn);

      const reloadBtn = document.createElement('button');
      reloadBtn.type = 'button';
      reloadBtn.className = 'btn ghost';
      reloadBtn.textContent = 'Reload from server';
      reloadBtn.addEventListener('click', reloadWhatsAppCsatPrompts);
      actions.appendChild(reloadBtn);

      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'btn';
      addBtn.textContent = 'Add prompt';
      addBtn.addEventListener('click', addWhatsAppCsatPrompt);
      actions.appendChild(addBtn);

      section.appendChild(actions);
      details.appendChild(section);
    }

    async function loadWhatsAppCsatPrompts() {
      if (channelSettingsState.loadingWhatsAppCsat) return;
      channelSettingsState.loadingWhatsAppCsat = true;
      channelSettingsState.whatsappCsatFetched = true;
      channelSettingsState.whatsappCsatLoadFailed = false;
      renderChannelConfigs();
      try {
        const response = await adminAPI.fetch(API.adminWhatsAppCsatPrompts, { method: 'GET' });
        const items = Array.isArray(response?.items) ? response.items : [];
        const defaults = Array.isArray(response?.defaults) ? response.defaults : [];
        channelSettingsState.whatsappCsatPrompts = items.map((item) => cloneWhatsAppPrompt(item));
        channelSettingsState.whatsappCsatDefaults = defaults.map((item) => cloneWhatsAppPrompt(item));
      } catch (err) {
        showFlash(err.message || 'Failed to load WhatsApp CSAT prompts', 'error');
        channelSettingsState.whatsappCsatPrompts = [];
        channelSettingsState.whatsappCsatLoadFailed = true;
        channelSettingsState.whatsappCsatFetched = false;
      } finally {
        channelSettingsState.loadingWhatsAppCsat = false;
        renderChannelConfigs();
      }
    }

    function reloadWhatsAppCsatPrompts() {
      if (channelSettingsState.loadingWhatsAppCsat) return;
      channelSettingsState.whatsappCsatLoadFailed = false;
      channelSettingsState.whatsappCsatFetched = false;
      loadWhatsAppCsatPrompts();
    }

    function addWhatsAppCsatPrompt() {
      const prompts = getWhatsAppCsatPrompts().map((item) => Object.assign({}, item));
      prompts.push({ code: `custom_${Date.now()}`, body_template: '', locale: 'en', enabled: true });
      channelSettingsState.whatsappCsatPrompts = prompts;
      renderChannelConfigs();
    }

    function removeWhatsAppCsatPrompt(index) {
      const prompts = getWhatsAppCsatPrompts().map((item) => Object.assign({}, item));
      prompts.splice(index, 1);
      channelSettingsState.whatsappCsatPrompts = prompts;
      renderChannelConfigs();
    }

    function resetWhatsAppCsatPromptsToDefaults() {
      const defaults = Array.isArray(channelSettingsState.whatsappCsatDefaults)
        && channelSettingsState.whatsappCsatDefaults.length
        ? channelSettingsState.whatsappCsatDefaults
        : [];
      channelSettingsState.whatsappCsatPrompts = defaults.map((item) => cloneWhatsAppPrompt(item));
      renderChannelConfigs();
    }

    async function saveWhatsAppCsatPrompts(button) {
      if (!button) return;
      const prompts = getWhatsAppCsatPrompts();
      if (!prompts.length) {
        showFlash('Add at least one prompt before saving.', 'error');
        return;
      }
      const payload = {
        prompts: prompts.map((item) => ({
          code: (item.code || '').trim(),
          body_template: item.body_template || '',
          locale: (item.locale || 'en').trim() || 'en',
          enabled: item.enabled !== false,
        })),
      };
      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Saving…';
      try {
        const response = await adminAPI.fetch(API.adminWhatsAppCsatPrompts, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        const items = Array.isArray(response?.items) ? response.items : payload.prompts;
        const defaults = Array.isArray(response?.defaults) ? response.defaults : channelSettingsState.whatsappCsatDefaults;
        channelSettingsState.whatsappCsatPrompts = items.map((item) => cloneWhatsAppPrompt(item));
        channelSettingsState.whatsappCsatDefaults = defaults.map((item) => cloneWhatsAppPrompt(item));
        showFlash('WhatsApp CSAT prompts saved.', 'success');
      } catch (err) {
        showFlash(err.message || 'Failed to save WhatsApp CSAT prompts', 'error');
      } finally {
        button.disabled = false;
        button.textContent = originalText;
        renderChannelConfigs();
      }
    }

export default {
  render: renderWhatsAppCsatPrompts,
  load: loadWhatsAppCsatPrompts,
  reload: reloadWhatsAppCsatPrompts,
  cloneWhatsAppPrompt,
  getPrompts: getWhatsAppCsatPrompts,
};
